import { BaseService, Unauthorized, UnexpectedError } from './base-service';


export interface ServerPool {
    id: string;
    type: 'rtmp' | 'rtsp';
    cloudProvider: 'azure_vmss' | 'bare_metal';
    serverCount: number;
}

export interface TimelineProvider {
    id: number;
    timelineType: 'azure_tables';
    tableName: string;
    createdAt: string;
    serverCount: number;
}

export interface StreamServer {
    id: number;
    domain: string;
    serverPoolId: string;
    timelineProviderId: number;
    videoStorageProviderId: number;
    state: 'waiting_server_config' | 'enabled' | 'manual_disabled' | 'waiting_for_deletion';
    createdAt: string;
    cameraCount: number;
    serverPoolToPurgeCamerasId: string | null;
}

export interface VideoStorageProvider {
    id: number;
    blobStorageType: 'aws' | 'azure';
    bucketName: string;
    createdAt: string;
    serverCount: number;
}

export interface ServerFarmInterface {
    companyName: string;
    defaultServerRtspPoolId: string | null;
    defaultServerRtmpPoolId: string | null;
    defaultTimelineProviderId: number | null;
    defaultVideoStorageProviderId: number | null;
    serverPools: ServerPool[];
    timelineProviders: TimelineProvider[];
    videoStorageProviders: VideoStorageProvider[];
    servers: StreamServer[];
}

export class StreamServersService extends BaseService {
    async getServerFarms(): Promise<{ id: number; companyId: number; companyName: string; }[]> {
        const token = await this.getToken();
        const res = await fetch(`${this.centralEndpoint}/internal-app/stream-server/server-farms`, {
            method: 'GET',
            headers: this.getHeaders(token, 'application/json;charset=utf-8'),
        });

        if (res.status == 401) {
            throw new Unauthorized();
        } else if (res.status != 200) {
            throw new UnexpectedError();
        }
        return res.json();
    }

    async getServerFarmById(serverFarmId: number): Promise<ServerFarmInterface> {
        const token = await this.getToken();
        const res = await fetch(`${this.centralEndpoint}/internal-app/stream-server/server-farm/${serverFarmId}`, {
            method: 'GET',
            headers: this.getHeaders(token, 'application/json;charset=utf-8'),
        });

        if (res.status == 401) {
            throw new Unauthorized();
        } else if (res.status != 200) {
            throw new UnexpectedError();
        }
        return res.json();
    }

    async setServerPoolAsDefault(serverPoolId: string) {
        const token = await this.getToken();
        const res = await fetch(`${this.centralEndpoint}/internal-app/stream-server/server-pool/${serverPoolId}/set-as-default`, {
            method: 'PUT',
            headers: this.getHeaders(token, 'application/json;charset=utf-8'),
        });

        if (res.status == 401) {
            throw new Unauthorized();
        } else if (res.status != 200) {
            throw new UnexpectedError();
        }
    }

    async setTimelineProviderAsDefault(timelineProviderId: number) {
        const token = await this.getToken();
        const res = await fetch(`${this.centralEndpoint}/internal-app/stream-server/timeline-provider/${timelineProviderId}/set-as-default`, {
            method: 'PUT',
            headers: this.getHeaders(token, 'application/json;charset=utf-8'),
        });

        if (res.status == 401) {
            throw new Unauthorized();
        } else if (res.status != 200) {
            throw new UnexpectedError();
        }
    }

    async setVideoStorageProviderAsDefault(videoStorageProviderId: number) {
        const token = await this.getToken();
        const res = await fetch(`${this.centralEndpoint}/internal-app/stream-server/video-storage-provider/${videoStorageProviderId}/set-as-default`, {
            method: 'PUT',
            headers: this.getHeaders(token, 'application/json;charset=utf-8'),
        });

        if (res.status == 401) {
            throw new Unauthorized();
        } else if (res.status != 200) {
            throw new UnexpectedError();
        }
    }

    async markServerToBeDeleted(serverId: number) {
        const token = await this.getToken();
        const res = await fetch(`${this.centralEndpoint}/internal-app/stream-server/server/${serverId}/mark-to-be-deleted`, {
            method: 'PUT',
            headers: this.getHeaders(token, 'application/json;charset=utf-8'),
        });

        if (res.status == 401) {
            throw new Unauthorized();
        } else if (res.status != 200) {
            throw new UnexpectedError();
        }
    }

    async disableServer(serverId: number) {
        const token = await this.getToken();
        const res = await fetch(`${this.centralEndpoint}/internal-app/stream-server/server/${serverId}/disable`, {
            method: 'PUT',
            headers: this.getHeaders(token, 'application/json;charset=utf-8'),
        });

        if (res.status == 401) {
            throw new Unauthorized();
        } else if (res.status != 200) {
            throw new UnexpectedError();
        }
    }

    async enableServer(serverId: number) {
        const token = await this.getToken();
        const res = await fetch(`${this.centralEndpoint}/internal-app/stream-server/server/${serverId}/enable`, {
            method: 'PUT',
            headers: this.getHeaders(token, 'application/json;charset=utf-8'),
        });

        if (res.status == 401) {
            throw new Unauthorized();
        } else if (res.status != 200) {
            throw new UnexpectedError();
        }
    }

    async purgeServerCamerasToServerPool(serverId: number, serverPoolId: string) {
        const token = await this.getToken();
        const res = await fetch(`${this.centralEndpoint}/internal-app/stream-server/server/${serverId}/purge-cameras/${serverPoolId}`, {
            method: 'PUT',
            headers: this.getHeaders(token, 'application/json;charset=utf-8'),
        });

        if (res.status == 401) {
            throw new Unauthorized();
        } else if (res.status != 200) {
            throw new UnexpectedError();
        }
    }
}

export const streamServersService = new StreamServersService();
